import { addDays, format, startOfWeek } from "date-fns";

import { TDayName, TDaysSettings, TDaySettings, TUserSettings } from "@/types";

/** Monday first: an object's key order is whatever the API sent. */
export const DAY_ORDER: TDayName[] = [
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
	"sunday",
];

/**
 * The week's days in order, skipping any the settings do not carry -
 * every display and editor reads the days through this, never through
 * Object.entries, which showed them shuffled.
 */
export const orderedWorkDays = (
	workDays: TDaysSettings | undefined
): [TDayName, TDaySettings][] =>
	DAY_ORDER.filter((day) => workDays?.[day]).map((day) => [
		day,
		(workDays as TDaysSettings)[day],
	]);

export const dayToNumberMap: { [key in TDayName]: number } = {
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
	sunday: 7,
};

export const getTranslatedDayName = (day: TDayName) => {
	// Adjust dayNumber to be 0-6 for date-fns compatibility
	const adjustedDayNumber = (dayToNumberMap[day] - 1) % 7;

	// Get the start of the week (default Sunday)
	const start = startOfWeek(new Date(), { weekStartsOn: 1 });

	// Add the adjusted day number to the start of the week
	const targetDate = addDays(start, adjustedDayNumber);

	// Format the target date to get the day name in the specified locale
	return format(targetDate, "EEEE");
};

/** "07:30" → 7.5; anything unparsable → 0. */
export const parseHours = (value: string | undefined): number => {
	if (!value) return 0;
	const [h, m] = value.split(":").map(Number);
	if (Number.isNaN(h)) return 0;
	return h + (Number.isNaN(m) ? 0 : m / 60);
};

/**
 * What a week looks like at this rhythm: worked hours (per-day hours,
 * or the daily default when a day has none) and the working-day count.
 * No amount: a rate is faced towards a client, not towards a week.
 */
export const weeklyTotals = (
	values: Pick<TUserSettings, "workDays" | "hoursPerDay">
) => {
	const workingDays = Object.values(values.workDays ?? {}).filter(
		(day) => day.isWorkingDay
	);
	const hours = workingDays.reduce(
		(sum, day) => sum + parseHours(day.hours || values.hoursPerDay || ""),
		0
	);
	const days = workingDays.length;

	return { hours: Math.round(hours * 10) / 10, days };
};
